import { createContext, useContext, useState, ReactNode } from 'react';

export type AlertType = 'ambulance' | 'patient_critical';

export interface GlobalAlert {
  id: string;
  type: AlertType;
  title: string;
  message: string;
  timestamp: number;
  metadata?: any;
}

interface AlertContextType {
  activeAlerts: GlobalAlert[];
  addAlert: (alert: Omit<GlobalAlert, 'id' | 'timestamp'>) => void;
  removeAlert: (id: string) => void;
  clearAllAlerts: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export function AlertProvider({ children }: { children: ReactNode }) {
  const [activeAlerts, setActiveAlerts] = useState<GlobalAlert[]>([]);

  const addAlert = (alert: Omit<GlobalAlert, 'id' | 'timestamp'>) => {
    const newAlert: GlobalAlert = {
      ...alert,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
    };
    setActiveAlerts((prev) => [newAlert, ...prev]);
  };

  const removeAlert = (id: string) => {
    setActiveAlerts((prev) => prev.filter((a) => a.id !== id));
  };

  const clearAllAlerts = () => {
    setActiveAlerts([]);
  };

  return (
    <AlertContext.Provider value={{ activeAlerts, addAlert, removeAlert, clearAllAlerts }}>
      {children}
    </AlertContext.Provider>
  );
}

export function useGlobalAlerts() {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('useGlobalAlerts must be used within an AlertProvider');
  }
  return context;
}
